import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from './user.service';
import { User, UserTenant } from './user.interface';
import dayjs from 'dayjs';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, TableModule],
  template: `
    <div class="dashboard">
      <main class="dashboard-content">
        <div class="content-container">
          <div *ngIf="isLoading" class="loading-state">
            <div class="loading-content">
              <i class="pi pi-spinner pi-spin"></i>
              <p>Loading users...</p>
            </div>
          </div>

          <div *ngIf="!isLoading && error" class="error-state">
            <p>{{ error }}</p>
          </div>

          <div *ngIf="!isLoading && !error" class="users-section">
            <p-table
              [value]="users"
              [tableStyle]="{'min-width': '1200px', 'width': '100%'}"
              [showGridlines]="true"
              sortField="name"
              [sortOrder]="1">

              <ng-template pTemplate="header">
                <tr>
                  <th pSortableColumn="name" style="width: 18%">
                    Name <p-sortIcon field="name"></p-sortIcon>
                  </th>
                  <th style="width: 21%">Email</th>
                  <th style="width: 19%">Tenants</th>
                  <th style="width: 14%">Created At</th>
                  <th style="width: 14%">Updated At</th>
                  <th style="width: 14%">Last Login</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-user>
                <tr>
                  <td>{{ user.name || 'N/A' }}</td>
                  <td>{{ user.email || 'N/A' }}</td>
                  <td>{{ formatTenants(user.tenants) }}</td>
                  <td>{{ formatDate(user.createdAt) }}</td>
                  <td>{{ formatDate(user.updatedAt) }}</td>
                  <td>{{ formatDate(user.lastLogin) }}</td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="6" style="text-align: center">No users found</td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [`
    .dashboard {
      min-height: calc(100vh - 60px);
      background-color: #f8f9fa;
    }

    .dashboard-content {
      padding: 2rem;
    }

    .content-container {
      max-width: 95%;
      margin: 0 auto;
    }

    .loading-state {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 200px;
    }

    .loading-content {
      text-align: center;
    }

    .loading-content i {
      font-size: 2rem;
      color: #021d7f;
      margin-bottom: 1rem;
    }

    .loading-content p {
      font-size: 1rem;
      color: #666;
    }

    .error-state {
      text-align: center;
      padding: 2rem;
      background: #fee;
      border: 1px solid #fcc;
      border-radius: 4px;
      color: #c33;
    }

    .users-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      padding: 1.5rem;
    }

    :host ::ng-deep .p-datatable {
      border: 1px solid #e0e0e0;
      border-radius: 6px;
    }

    :host ::ng-deep .p-datatable-header {
      background: #f8f9fa;
      border-bottom: 2px solid #e0e0e0;
    }

    :host ::ng-deep .p-datatable-thead > tr > th {
      background: #f8f9fa;
      color: #021d7f;
      font-weight: 600;
      border-bottom: 2px solid #e0e0e0;
      padding: 1rem;
    }

    :host ::ng-deep .p-datatable-tbody > tr > td {
      padding: 1rem;
      border-bottom: 1px solid #f0f0f0;
      vertical-align: top;
    }

    :host ::ng-deep .p-datatable-tbody > tr:hover {
      background: #f8f9fa;
    }

    .tenant-info {
      display: block;
      margin-bottom: 0.25rem;
      font-size: 0.9rem;
    }

    .tenant-info:last-child {
      margin-bottom: 0;
    }
  `]
})
export class UsersComponent implements OnInit, OnDestroy {
  users: User[] = [];
  isLoading = true;
  error: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(private userService: UserService) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUsers(): void {
    this.isLoading = true;
    this.error = null;

    this.userService.getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.users = users;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading users:', error);
          this.error = 'Failed to load users. Please try again later.';
          this.isLoading = false;
        }
      });
  }

  formatDate(dateString?: string): string {
    if (!dateString) {
      return 'N/A';
    }

    try {
      return dayjs(dateString).format('YYYY/MM/DD');
    } catch {
      return 'N/A';
    }
  }

  formatTenants(tenants?: UserTenant[]): string {
    if (!tenants || tenants.length === 0) {
      return 'N/A';
    }

    return tenants.map(tenant => 
      `${tenant.tenantId}`
    ).join(', ');
  }
}
